import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { TextField, Tooltip } from '@mui/material'
import React from 'react'
import { filterSx } from '../../../components/filter-fields'
import { listSearchSx } from '../../../components/list-styles'
import MonthlyBarChart from '../../../components/MonthlyBarChart'
import { ClipText, ReportKpi } from '../../../components/report-parts'
import ReportTable from '../../../components/ReportTable'
import type { ReportTableColumn } from '../../../components/ReportTable'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import PolicyDrawer from '../../policies/drawer/policy-drawer'
import { formatAmount, formatCount, PAGE_SIZES } from '../common/report-common'
import { COMISION_COLOR, PREVIOUS_COLOR, PRIMA_COLOR, useReportCommissionsModel } from './report-commissions-model'
import type { CommissionReceiptRow } from './report-commissions-model'

const monthSx = { ...filterSx, width: { xs: '100%', sm: 190 } }

const ReportCommissions = () => {
    const model = useReportCommissionsModel()
    const { totals } = model
    const up = totals.diferencia >= 0

    const columns: ReportTableColumn<CommissionReceiptRow>[] = [
        {
            key: 'ver',
            label: '',
            render: (row) => (
                <Tooltip title="Ver póliza">
                    <button type="button" disabled={!row.polizaId} aria-label={`Ver póliza ${row.noPoliza}`} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-k-primary-ink hover:bg-k-accent-soft disabled:opacity-40" onClick={() => model.openPoliza(row.polizaId)}>
                        <VisibilityOutlinedIcon style={{ fontSize: 18 }} />
                    </button>
                </Tooltip>
            ),
        },
        {
            key: 'noPoliza',
            label: 'Póliza',
            minWidth: 160,
            render: (row) => (
                <span className="flex items-center gap-1.5">
                    <span className="flex flex-col">
                        <span className="font-bold text-k-ink">{row.noPoliza || '—'}</span>
                        <span className="text-xs tabular-nums text-k-muted">Recibo {row.serie}</span>
                    </span>
                    {row.robot && (
                        <Tooltip title="Póliza creada por la sincronización automática">
                            <SmartToyOutlinedIcon className="text-tone-orange-ink" style={{ fontSize: 16 }} />
                        </Tooltip>
                    )}
                </span>
            ),
        },
        { key: 'cliente', label: 'Cliente', minWidth: 200, render: (row) => <ClipText text={row.cliente} maxWidth={220} /> },
        { key: 'vendedor', label: 'Vendedor', minWidth: 180, render: (row) => <ClipText text={row.vendedor} maxWidth={220} /> },
        { key: 'aseguradora', label: 'Aseguradora', render: (row) => <span className="font-semibold text-k-ink">{row.aseguradora || '—'}</span> },
        { key: 'total', label: 'Total', align: 'right', render: (row) => <span className="whitespace-nowrap">${formatAmount(row.total)} <span className="text-xs text-k-muted">{row.moneda}</span></span> },
        { key: 'comision', label: 'Comisión', align: 'right', render: (row) => <span className="whitespace-nowrap font-bold text-k-ok">${formatAmount(row.comision)} <span className="text-xs font-normal text-k-muted">{row.moneda}</span></span> },
        { key: 'vigencia', label: 'Vigencia', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-ink-2">{row.vigenciaDe} – {row.vigenciaA}</span> },
        { key: 'vence', label: 'Vencimiento', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-ink-2">{row.vence || '—'}</span> },
        { key: 'pago', label: 'Pago', render: (row) => <span className="whitespace-nowrap font-semibold tabular-nums text-k-ink">{row.pago || '—'}</span> },
    ]

    return (
        <KarteraAdminTemplate loading={false} title="Reporte de comisiones">
            <PolicyDrawer polizaId={model.detailId} onClose={model.closePoliza} />

            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title="Reporte de comisiones"
                    description="Primas y comisiones por mes, comparadas con el mismo periodo del año anterior."
                    actions={(
                        <button
                            type="button"
                            className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                            onClick={model.reload}
                        >
                            <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                            Actualizar
                        </button>
                    )}
                />

                <section className="k-card gap-3">
                    <div className="flex flex-wrap items-start gap-2.5">
                        <TextField
                            type="month" size="small" label="Desde" sx={monthSx}
                            value={model.startMonth}
                            error={model.rangeInvalid}
                            slotProps={{ inputLabel: { shrink: true } }}
                            onChange={(event) => model.changeStartMonth(event.target.value)}
                        />
                        <TextField
                            type="month" size="small" label="Hasta" sx={monthSx}
                            value={model.endMonth}
                            error={model.rangeInvalid}
                            slotProps={{ inputLabel: { shrink: true } }}
                            onChange={(event) => model.changeEndMonth(event.target.value)}
                        />
                    </div>
                    {model.rangeError && <p className="m-0 text-xs font-semibold text-k-danger-ink">{model.rangeError}</p>}
                </section>

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                <section className={`k-card ${model.loading ? 'opacity-60' : ''}`}>
                    <h2 className="k-card-title">Comisiones vs primas</h2>
                    <MonthlyBarChart
                        series={[{ name: 'Prima neta', color: PRIMA_COLOR }, { name: 'Comisiones', color: COMISION_COLOR }]}
                        points={model.primaVsComision}
                        formatValue={model.formatMoney}
                        formatAxis={model.formatAxis}
                    />
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <ReportKpi label="Prima neta acumulada" value={model.formatMoney(totals.primas)} />
                        <ReportKpi label="Comisiones acumuladas" value={model.formatMoney(totals.comisiones)} />
                        <ReportKpi label="Comisión promedio" value={`${totals.porcentaje.toLocaleString('es-MX', { maximumFractionDigits: 1 })}%`} hint="Comisiones sobre prima neta" />
                    </div>
                </section>

                <section className={`k-card ${model.loading ? 'opacity-60' : ''}`}>
                    <h2 className="k-card-title">Comisiones vs periodo anterior</h2>
                    <MonthlyBarChart
                        series={[{ name: 'Periodo actual', color: COMISION_COLOR }, { name: 'Mismo mes del año anterior', color: PREVIOUS_COLOR }]}
                        points={model.vsAnterior}
                        formatValue={model.formatMoney}
                        formatAxis={model.formatAxis}
                    />
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <ReportKpi label="Comisiones periodo actual" value={model.formatMoney(totals.comisiones)} hint={`Año anterior: ${model.formatMoney(totals.anteriores)}`} />
                        <div className="flex flex-col gap-1 rounded-2xl border border-k-line bg-k-surface px-4 py-3.5">
                            <span className="text-xs font-bold uppercase tracking-[0.05em] text-k-muted">Diferencia vs periodo anterior</span>
                            <span className={`inline-flex items-center gap-1 text-[22px] font-extrabold tabular-nums tracking-[-0.02em] ${up ? 'text-k-ok' : 'text-k-danger-ink'}`}>
                                {up ? <ArrowUpwardIcon style={{ fontSize: 20 }} /> : <ArrowDownwardIcon style={{ fontSize: 20 }} />}
                                {model.formatMoney(Math.abs(totals.diferencia))}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1 rounded-2xl border border-k-line bg-k-surface px-4 py-3.5">
                            <span className="text-xs font-bold uppercase tracking-[0.05em] text-k-muted">Variación porcentual</span>
                            {totals.diferenciaPct == null ? (
                                <span className="text-[22px] font-extrabold text-k-muted">—</span>
                            ) : (
                                <span className={`inline-flex items-center gap-1 text-[22px] font-extrabold tabular-nums tracking-[-0.02em] ${totals.diferenciaPct >= 0 ? 'text-k-ok' : 'text-k-danger-ink'}`}>
                                    {totals.diferenciaPct >= 0 ? <ArrowUpwardIcon style={{ fontSize: 20 }} /> : <ArrowDownwardIcon style={{ fontSize: 20 }} />}
                                    {Math.abs(totals.diferenciaPct).toLocaleString('es-MX', { maximumFractionDigits: 1 })}%
                                </span>
                            )}
                            {totals.diferenciaPct == null && <span className="text-xs text-k-muted">Sin comisiones en el periodo anterior</span>}
                        </div>
                    </div>
                </section>

                <ReportTable<CommissionReceiptRow>
                    columns={columns}
                    rows={model.rows}
                    rowKey={(row) => row.id}
                    emptyText={model.loading ? 'Cargando recibos…' : 'No hay recibos pagados en el periodo.'}
                    loading={model.loading}
                    page={model.page}
                    totalPages={model.totalPages}
                    pages={model.pages}
                    pageSize={model.pageSize}
                    pageSizes={PAGE_SIZES}
                    rangeFrom={model.rangeFrom}
                    rangeTo={model.rangeTo}
                    totalRows={model.filteredCount}
                    noun="recibos pagados"
                    formatCount={formatCount}
                    onPage={model.setPage}
                    onPageSize={model.changePageSize}
                    toolbar={(
                        <>
                            <h2 className="k-card-title mr-2">Recibos pagados</h2>
                            <div className="min-w-0 flex-1">
                                <TextField
                                    label="Buscar en la tabla"
                                    size="small"
                                    value={model.search}
                                    sx={listSearchSx}
                                    slotProps={{ htmlInput: { autoComplete: 'off' } }}
                                    onChange={(event) => model.changeSearch(event.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                disabled={model.filteredCount === 0}
                                onClick={model.exportCsv}
                                className="inline-flex h-10 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <DownloadOutlinedIcon style={{ fontSize: 18 }} />
                                Exportar CSV
                            </button>
                        </>
                    )}
                />
            </div>
        </KarteraAdminTemplate>
    )
}

export default ReportCommissions
